import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth debe usarse dentro de AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  // Also keep a ref for immediate access in callbacks
  const tokenRef = useRef(null);

  // Sync token state with ref
  const updateToken = (newToken) => {
    tokenRef.current = newToken;
    setToken(newToken);
    if (newToken) {
      sessionStorage.setItem('_at', newToken);
    } else {
      sessionStorage.removeItem('_at');
    }
  };

  // Verify token on mount using token from memory
  const verifyToken = useCallback(async () => {
    // First try to get from sessionStorage (survives page refresh but not tab close)
    const storedToken = sessionStorage.getItem('_at');
    if (storedToken) {
      tokenRef.current = storedToken;
      setToken(storedToken);
    }
    
    if (!tokenRef.current) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/me`, {
        headers: {
          'Authorization': `Bearer ${tokenRef.current}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdmin(data);
      } else {
        // Token invalid or expired - clear state
        updateToken(null);
        setAdmin(null);
      }
    } catch (error) {
      console.error('Error verifying admin token:', error);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        // Store token using updateToken helper
        updateToken(data.access_token);
        setAdmin(data.user);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Error de autenticación' };
      }
    } catch (error) {
      return { success: false, error: 'Error de conexión' };
    }
  };

  const logout = async () => {
    try {
      // Clear server-side session if needed
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenRef.current}`
        }
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
    updateToken(null);
    setAdmin(null);
  };

  // Fetch with auth helper - uses token from memory
  const fetchWithAuth = async (url, options = {}) => {
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${tokenRef.current}`
    };

    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(options.body);
    }

    const response = await fetch(`${API_URL}${url}`, {
      ...options,
      headers
    });

    if (response.status === 401) {
      updateToken(null);
      setAdmin(null);
      throw new Error('Sesión expirada');
    }

    return response;
  };

  const value = {
    admin,
    loading,
    isAuthenticated: !!admin,
    token,
    login,
    logout,
    fetchWithAuth
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
